/**
 * DOVE-2667 — a driver returns an order from the Toolbox as Refused or Rescheduled. camelCase
 * models for OrderReturnResource, `GET order-returns/options`, the board tile `return` block, the
 * Communications `order_summary`, the reassign driver picker and the schedule history. The
 * constructors map the API's snake_case; the services build them.
 */

/** `orders.order_status_id` values the driver's return writes. */
export const ORDER_STATUS_ID_REFUSED = 17;
export const ORDER_STATUS_ID_RESCHEDULED = 18;
export const RETURNED_ORDER_STATUS_IDS: readonly number[] = [ORDER_STATUS_ID_REFUSED, ORDER_STATUS_ID_RESCHEDULED];

/** PST note subjects written by the driver's return. */
export const RETURN_NOTE_SUBJECTS: readonly string[] = ['Order Refused', 'Order Rescheduled'];

export type OrderReturnType = 'refuse' | 'reschedule';
export type OrderReturnDispatchFlag = 'none' | 'findings' | 'reassigned';
export type OrderReturnResolution = 'rescheduled' | 'cancelled' | 'reassigned';
export type DispatchOutcome = 'refusal_confirmed' | 'reschedule_confirmed' | 'wants_today' | 'could_not_reach';

/** True for order status ids 17 (Refused) / 18 (Rescheduled). */
export function isReturnedOrderStatus(orderStatusId: number | null | undefined): boolean {
	return orderStatusId != null && RETURNED_ORDER_STATUS_IDS.includes(Number(orderStatusId));
}

// ── State → tone (the one place DOVE-2667 colors live) ─────────────────────────────────────────

export type ReturnTone = 'refused' | 'rescheduled' | 'findings' | 'resolved' | 'neutral';

interface IReturnToneColors {
	text: string;
	background: string;
	accent: string;
}

/** theme.css variables per state; chips, pins, legend, badges, banners and the drawer read these. */
export const RETURN_TONES: Record<ReturnTone, IReturnToneColors> = {
	refused: { text: 'var(--red-700)', background: 'var(--red-50)', accent: 'var(--red-500)' },
	rescheduled: { text: 'var(--orange-700)', background: 'var(--orange-50)', accent: 'var(--orange-500)' },
	findings: { text: 'var(--indigo-700)', background: 'var(--indigo-50)', accent: 'var(--indigo-500)' },
	resolved: { text: 'var(--green-700)', background: 'var(--green-50)', accent: 'var(--green-500)' },
	neutral: { text: 'var(--text-color-secondary)', background: 'var(--surface-100)', accent: 'var(--gray-400)' }
};

/** A returned order's pin body; the ring is the state accent. */
export const RETURNED_PIN_FILL = 'var(--bluegray-700)';

export function returnTone(type: OrderReturnType | null | undefined): ReturnTone | null {
	if (type === 'refuse') return 'refused';
	if (type === 'reschedule') return 'rescheduled';
	return null;
}

export function dispatchFlagTone(flag: OrderReturnDispatchFlag | null | undefined): ReturnTone | null {
	if (flag === 'findings') return 'findings';
	if (flag === 'reassigned') return 'resolved';
	return null;
}

export function scheduleEventTone(kind: string): ReturnTone {
	switch (kind) {
		case 'refused':
		case 'cancelled':
			return 'refused';
		case 'rescheduled':
			return 'rescheduled';
		case 'findings':
			return 'findings';
		case 'reassigned':
		case 'cs_rescheduled':
			return 'resolved';
		default:
			return 'neutral';
	}
}

export function returnChipStyle(tone: ReturnTone | null | undefined): Record<string, string> {
	const colors = RETURN_TONES[tone || 'neutral'];
	return { background: colors.background, color: colors.text };
}

export function returnDotStyle(tone: ReturnTone | null | undefined): Record<string, string> {
	return { background: RETURN_TONES[tone || 'neutral'].accent };
}

export function returnBannerStyle(tone: ReturnTone | null | undefined): Record<string, string> {
	const colors = RETURN_TONES[tone || 'neutral'];
	return { background: colors.background, color: colors.text, 'border-left-color': colors.accent };
}

// ── Models ─────────────────────────────────────────────────────────────────────────────────────

export interface IOrderReturnUser {
	id: number;
	name: string;
}

function toReturnUser(data: any): IOrderReturnUser | null {
	return data ? { id: data.id, name: data.name } : null;
}

/** One entry of a `GET order-returns/options` list. `value` is sent back; `label` is display text. */
export interface IOrderReturnOption {
	value: string;
	label: string;
	relationshipRequired?: boolean;
	noAnswer?: boolean;
}

function toOptions(list: any): IOrderReturnOption[] {
	return (Array.isArray(list) ? list : []).map((o: any) => ({
		value: o.value,
		label: o.label,
		relationshipRequired: o.relationship_required,
		noAnswer: o.no_answer
	}));
}

export class OrderReturnOptions {
	refuseReasons: IOrderReturnOption[];
	rescheduleReasons: IOrderReturnOption[];
	requesterTypes: IOrderReturnOption[];
	csCancelReasons: IOrderReturnOption[];
	dispatchOutcomes: IOrderReturnOption[];

	constructor(data: any) {
		this.refuseReasons = toOptions(data?.refuse_reasons);
		this.rescheduleReasons = toOptions(data?.reschedule_reasons);
		this.requesterTypes = toOptions(data?.requester_types);
		this.csCancelReasons = toOptions(data?.cs_cancel_reasons);
		this.dispatchOutcomes = toOptions(data?.dispatch_outcomes);
	}
}

export interface IOrderReturnFindings {
	reached: boolean;
	outcome: DispatchOutcome;
	outcomeLabel: string;
	spokeWith: string | null;
	relationship: string | null;
	text: string;
	by: IOrderReturnUser | null;
	at: string;
}

export interface IOrderReturnResolutionDetail {
	driverTruckAssignmentId: number | null;
	driver: IOrderReturnUser | null;
	scheduledDate: string | null;
	window: string | null;
	spokeWith: string | null;
	relationship: string | null;
	cancelReason: string | null;
	cancelReasonLabel: string | null;
	note: string | null;
}

/** OrderReturnResource. */
export class OrderReturn {
	id: number;
	orderId: number;
	type: OrderReturnType;
	typeLabel: string;
	driver: IOrderReturnUser | null;
	driverTruckId: number | null;
	byType: string | null;
	byTypeLabel: string | null;
	name: string | null;
	relationship: string | null;
	reason: string;
	reasonLabel: string;
	details: string;
	/** Branch-local `Y-m-d`. */
	preferredDate: string | null;
	/** `H:i`; both or neither. */
	preferredWindowStart: string | null;
	preferredWindowEnd: string | null;
	/** `8:00 AM – 12:00 PM`; null when there is no window. */
	preferredWindowLabel: string | null;
	documentId: number | null;
	noteId: number | null;
	occurredAt: string;
	dispatchFlag: OrderReturnDispatchFlag;
	findings: IOrderReturnFindings | null;
	resolution: OrderReturnResolution | null;
	resolvedAt: string | null;
	resolvedBy: IOrderReturnUser | null;
	resolutionDetail: IOrderReturnResolutionDetail | null;
	/** The order's branch timezone (IANA); `*_at` values display on this clock. */
	timezone: string | null;
	createdAt: string;

	constructor(data: any) {
		this.id = data.id;
		this.orderId = data.order_id;
		this.type = data.type;
		this.typeLabel = data.type_label;
		this.driver = toReturnUser(data.driver);
		this.driverTruckId = data.driver_truck_id ?? null;
		this.byType = data.by_type ?? null;
		this.byTypeLabel = data.by_type_label ?? null;
		this.name = data.name ?? null;
		this.relationship = data.relationship ?? null;
		this.reason = data.reason;
		this.reasonLabel = data.reason_label;
		this.details = data.details ?? '';
		this.preferredDate = data.preferred_date ?? null;
		this.preferredWindowStart = data.preferred_window_start ?? null;
		this.preferredWindowEnd = data.preferred_window_end ?? null;
		this.preferredWindowLabel = data.preferred_window_label ?? null;
		this.documentId = data.document_id ?? null;
		this.noteId = data.note_id ?? null;
		this.occurredAt = data.occurred_at;
		this.dispatchFlag = data.dispatch_flag ?? 'none';
		this.findings = data.findings
			? {
					reached: !!data.findings.reached,
					outcome: data.findings.outcome,
					outcomeLabel: data.findings.outcome_label,
					spokeWith: data.findings.spoke_with ?? null,
					relationship: data.findings.relationship ?? null,
					text: data.findings.text ?? '',
					by: toReturnUser(data.findings.by),
					at: data.findings.at
				}
			: null;
		this.resolution = data.resolution ?? null;
		this.resolvedAt = data.resolved_at ?? null;
		this.resolvedBy = toReturnUser(data.resolved_by);
		const detail = data.resolution_detail;
		this.resolutionDetail = detail
			? {
					driverTruckAssignmentId: detail.driver_truck_assignment_id ?? null,
					driver: toReturnUser(detail.driver),
					scheduledDate: detail.scheduled_date ?? null,
					window: detail.window ?? null,
					spokeWith: detail.spoke_with ?? null,
					relationship: detail.relationship ?? null,
					cancelReason: detail.cancel_reason ?? null,
					cancelReasonLabel: detail.cancel_reason_label ?? null,
					note: detail.note ?? null
				}
			: null;
		this.timezone = data.timezone ?? null;
		this.createdAt = data.created_at;
	}
}

/** The `return` block on a dispatch board tile (DispatchOrderResource); null unless OS is 17/18. */
export class DispatchOrderReturn {
	type: OrderReturnType;
	typeLabel: string;
	reasonLabel: string | null;
	returnedByName: string | null;
	returnedAt: string | null;
	dispatchFlag: OrderReturnDispatchFlag;
	timezone: string | null;

	constructor(data: any) {
		this.type = data.type;
		this.typeLabel = data.type_label;
		this.reasonLabel = data.reason_label ?? null;
		this.returnedByName = data.returned_by_name ?? null;
		this.returnedAt = data.returned_at ?? null;
		this.dispatchFlag = data.dispatch_flag ?? 'none';
		this.timezone = data.timezone ?? null;
	}
}

/** `GET dispatch/orders/{order}`: what the dispatcher dialog and the email deep link need. */
export class DispatchOrderReturnContext {
	orderId: number;
	branchId: number | null;
	orderStatusId: number | null;
	/** Branch-local `Y-m-d` (the API's bare `scheduled_at`). */
	scheduledDate: string | null;
	returns: OrderReturn[];
	latestReturn: OrderReturn | null;

	constructor(data: any) {
		this.orderId = data.id;
		this.branchId = data.branch_id ?? null;
		this.orderStatusId = data.order_status_id ?? null;
		this.scheduledDate = data.scheduled_at ? String(data.scheduled_at).slice(0, 10) : null;
		this.returns = (Array.isArray(data.returns) ? data.returns : []).map((r: any) => new OrderReturn(r));
		this.latestReturn = data.latest_return ? new OrderReturn(data.latest_return) : null;
	}
}

/** `order_summary` on the Communications note detail (null for patient notes). */
export class ReturnOrderSummary {
	id: number;
	type: string | null;
	/** Branch-local `Y-m-d`. */
	scheduledDate: string | null;
	orderDeliveryWindowId: number | null;
	deliveryWindow: string | null;
	orderStatusId: number | null;
	statusLabel: string | null;
	orderedBy: string | null;
	insurance: string | null;
	address: string;
	patientLocationHistoryId: number | null;
	items: { id: number; description: string; quantity: number }[];
	comments: { comment: string; createdAt: string }[];

	constructor(data: any) {
		this.id = data.id;
		this.type = data.type ?? null;
		this.scheduledDate = data.scheduled_date ?? null;
		this.orderDeliveryWindowId = data.order_delivery_window_id ?? null;
		this.deliveryWindow = data.delivery_window ?? null;
		this.orderStatusId = data.order_status_id ?? null;
		this.statusLabel = data.status_label ?? null;
		this.orderedBy = data.ordered_by ?? null;
		this.insurance = data.insurance ?? null;
		this.address = ReturnOrderSummary.formatAddress(data.address);
		this.patientLocationHistoryId = data.patient_location_history_id ?? null;
		this.items = (data.items || []).map((i: any) => ({
			id: i.id,
			description: i.description,
			quantity: i.quantity
		}));
		this.comments = (data.comments || []).map((c: any) => ({ comment: c.comment, createdAt: c.created_at }));
	}

	private static formatAddress(a: any): string {
		if (!a) return '';
		const street = [a.address_1, a.address_2].filter(Boolean).join(' ');
		const stateZip = [a.state, a.zipcode].filter(Boolean).join(' ');
		const cityLine = [a.city, stateZip].filter(Boolean).join(', ');
		return [a.facility_name, street, cityLine].filter(Boolean).join(', ');
	}
}

/** `GET dispatch/orders/{order}/return-reassignment/drivers`; `value` is the driver_truck_assignment_id. */
export class ReturnReassignDriver {
	value: number;
	label: string;
	truckName: string | null;
	stopCount: number;

	constructor(data: any) {
		this.value = data.value;
		this.label = data.label;
		this.truckName = data.truck_name ?? null;
		this.stopCount = data.stop_count ?? 0;
	}
}

/** One event of `GET orders/{order}/schedule-history`. */
export class ScheduleHistoryEvent {
	at: string;
	kind: string;
	label: string;
	detail: string | null;
	actorName: string | null;
	actorRole: string | null;

	constructor(data: any) {
		this.at = data.at;
		this.kind = data.kind;
		this.label = data.label;
		this.detail = data.detail ?? null;
		this.actorName = data.actor_name ?? null;
		this.actorRole = data.actor_role ?? null;
	}
}

export class ScheduleHistory {
	orderId: number;
	/** Newest first, like the mockup and the driver app (the API sends oldest first). */
	events: ScheduleHistoryEvent[];
	timezone: string | null;

	constructor(data: any) {
		this.orderId = data?.order_id;
		this.events = (data?.events || []).map((e: any) => new ScheduleHistoryEvent(e)).reverse();
		this.timezone = data?.timezone ?? null;
	}
}

// ── Requests (camelCase; the service writes the snake_case body) ─────────────────────────────────

export interface IReturnFindingsRequest {
	reached: boolean;
	outcome: DispatchOutcome;
	spokeWith: string | null;
	relationship: string | null;
	findings: string;
}

export interface IReturnRescheduleRequest {
	action: 'reschedule';
	/** Branch-local `Y-m-d`. */
	scheduledDate: string;
	/** `HH:mm`; both null = Anytime. */
	customStart: string | null;
	customEnd: string | null;
	spokeWith: string;
	relationship: string | null;
	note: string;
}

export interface IReturnCancelRequest {
	action: 'cancel';
	cancelReason: string;
	details: string;
}

export type IReturnResolutionRequest = IReturnRescheduleRequest | IReturnCancelRequest;
